/**
 * Lightweight analytics fan-out:
 *   1) PostHog (already loaded in index.html via window.posthog)
 *   2) Server-side log to /api/analytics/event for our own DB
 *   3) Optional Meta Pixel (window.fbq) and Google Analytics (window.gtag) if loaded
 *
 * Usage: track("checkout_clicked", { plan_id: "advanced_annual" })
 *
 * PIXEL SAFETY:
 *   3rd-party pixels (Meta / GA / TikTok) receive ONLY scrubbed properties from a
 *   strict allowlist below. Free-form text (messages, journal entries, goals,
 *   reflections, transcripts) is NEVER forwarded to advertisers, no matter what
 *   any future track() call passes in. PostHog and our own server log keep the
 *   full payload — those we control.
 */
import axios from "axios";
import { API } from "../auth/AuthContext";

// Strict allowlist of property keys safe to forward to third-party ad pixels.
// Anything outside this list is dropped silently.
const PIXEL_SAFE_KEYS = new Set([
  "plan_id",
  "plan",
  "amount",
  "currency",
  "interval",
  "tier",
  "channel",
  "mode",
  "spot",
  "page",
  "variant",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "ts",
  "authed",
  "step",                // onboarding step name only (e.g. "life", "focus") — no answer text
  "question_index",
  "day",
  "streak",
  "horizon",
  "email_domain",        // domain only — never full email
]);

function scrubForPixel(properties = {}) {
  const out = {};
  for (const [k, v] of Object.entries(properties)) {
    if (!PIXEL_SAFE_KEYS.has(k)) continue;
    // Only allow primitives — no objects/arrays that could smuggle text
    if (v == null || ["string", "number", "boolean"].includes(typeof v)) {
      // Cap any string value at 80 chars defensively
      out[k] = typeof v === "string" ? v.slice(0, 80) : v;
    }
  }
  return out;
}

export function track(event, properties = {}) {
  const props = { ...properties, ts: Date.now() };
  const pixelProps = scrubForPixel(props);

  // 1) PostHog (already initialized in index.html) — full payload, we control it
  try {
    if (typeof window !== "undefined" && window.posthog && window.posthog.capture) {
      window.posthog.capture(event, props);
    }
  } catch (e) {}

  // 2) Meta Pixel — SCRUBBED payload only
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      const META_MAP = {
        signup_completed: "CompleteRegistration",
        checkout_clicked: "InitiateCheckout",
        checkout_completed: "Purchase",
      };
      const metaEvent = META_MAP[event];
      if (metaEvent) {
        window.fbq("track", metaEvent, pixelProps);
      } else {
        window.fbq("trackCustom", event, pixelProps);
      }
    }
  } catch (e) {}

  // 3) Google Analytics — SCRUBBED payload only
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", event, pixelProps);
    }
  } catch (e) {}

  // 4) TikTok Pixel — SCRUBBED payload only
  try {
    if (typeof window !== "undefined" && window.ttq && typeof window.ttq.track === "function") {
      window.ttq.track(event, pixelProps);
    }
  } catch (e) {}

  // 5) Server-side persist (best-effort, fire-and-forget) — full payload, our DB
  try {
    axios.post(`${API}/analytics/event`, { event, properties: props }).catch(() => {});
  } catch (e) {}
}

export function identify(userId, traits = {}) {
  try {
    if (typeof window !== "undefined" && window.posthog && window.posthog.identify) {
      window.posthog.identify(userId, traits);
    }
  } catch (e) {}
}

export function pageView(name, properties = {}) {
  track("page_view", { page: name, ...properties });
}
